import React, { useState } from 'react';
import { Phone, Star, Calendar, Clock, ChevronLeft, ChevronRight, Check, User, Mail, Building, X, Plus } from 'lucide-react';
import { Avatar } from '@clareefai/ui';

const OneToOneCallWidget: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Array<{date: Date | null, time: string | null}>>([
    { date: null, time: null },
    { date: null, time: null },
    { date: null, time: null }
  ]);
  const [activeCalendar, setActiveCalendar] = useState<number | null>(null);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Mock promoter data
  const promoter = {
    name: 'Marie Leroy',
    company: 'Digital Agency',
    avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150',
    rating: 5,
    review: 'A revolutionary solution for our agency. Since we started using Clareefai, our productivity has increased by 40%.',
    product: 'Clareefai Platform'
  };

  const availableHours = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
  ];

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    console.log('Call request submitted:', {
      promoter: promoter.name,
      options: selectedOptions,
      prospect: formData
    });
    setCurrentStep(4);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Calendar functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Monday = 0
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isSameDate = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentCalendarDate);
    if (direction === 'prev') {
      newDate.setMonth(currentCalendarDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentCalendarDate.getMonth() + 1);
    }
    setCurrentCalendarDate(newDate);
  };

  const handleDateSelect = (day: number, optionIndex: number) => {
    const selectedDate = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth(), day);
    if (!isPastDate(selectedDate)) {
      const newOptions = [...selectedOptions];
      newOptions[optionIndex] = { ...newOptions[optionIndex], date: selectedDate, time: null };
      setSelectedOptions(newOptions);
    }
  };

  const handleTimeSelect = (time: string, optionIndex: number) => {
    const newOptions = [...selectedOptions];
    newOptions[optionIndex] = { ...newOptions[optionIndex], time };
    setSelectedOptions(newOptions);
    setActiveCalendar(null); // Close calendar after time selection
  };

  const handleRemoveOption = (optionIndex: number) => {
    const newOptions = [...selectedOptions];
    newOptions[optionIndex] = { date: null, time: null };
    setSelectedOptions(newOptions);
  };

  const renderCalendar = (optionIndex: number) => {
    const daysInMonth = getDaysInMonth(currentCalendarDate);
    const firstDay = getFirstDayOfMonth(currentCalendarDate);
    const days = [];

    // Empty days at the beginning
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth(), day);
      const isSelected = selectedOptions[optionIndex].date && isSameDate(date, selectedOptions[optionIndex].date!);
      const isPast = isPastDate(date);
      const isTodayDate = isToday(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day, optionIndex)}
          disabled={isPast}
          className={`h-8 w-8 rounded-lg text-xs font-medium transition-all duration-200 ${
            isSelected
              ? 'bg-blue-600 text-white shadow-lg scale-110'
              : isPast
              ? 'text-gray-300 cursor-not-allowed'
              : isTodayDate
              ? 'bg-blue-100 text-blue-800 border border-blue-400 hover:bg-blue-200'
              : 'text-gray-700 hover:bg-blue-100 hover:text-blue-700'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Talk to our customer</h3>
        <p className="text-gray-600">Get insights from real customers who use our solution daily</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center space-x-4">
          <Avatar
            src={promoter.avatar}
            name={promoter.name}
            size="lg"
            className="border-4 border-white shadow-lg"
          />
          <div className="flex-1">
            <h4 className="text-lg font-bold text-gray-900">{promoter.name}</h4>
            <p className="text-gray-600 flex items-center">
              <Building className="w-4 h-4 mr-1" />
              {promoter.company}
            </p>
            <div className="flex items-center space-x-1 mt-2">
              {[...Array(promoter.rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
              ))}
              <span className="text-sm text-gray-600 ml-1">({promoter.rating}/5)</span>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
          <p className="text-gray-800 italic text-sm">"{promoter.review}"</p>
          <p className="text-xs text-gray-500 mt-2">Using: {promoter.product}</p>
        </div>
      </div>

      <button
        onClick={handleNext}
        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2"
      >
        <Phone className="w-5 h-5" />
        <span>Request a call</span>
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Please suggest days and timeslot for a meeting with {promoter.name}</h3>
        <p className="text-gray-600">Choose up to 3 preferred options</p>
      </div>

      <div className="space-y-4">
        {selectedOptions.map((option, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Option {index + 1}:</h4>
              {option.date && option.time && (
                <button
                  onClick={() => handleRemoveOption(index)}
                  className="text-red-600 hover:text-red-800 p-1 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {!option.date || !option.time ? (
              <button
                onClick={() => setActiveCalendar(activeCalendar === index ? null : index)}
                className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Add a time</span>
              </button>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 font-medium text-center">
                  {option.date.toLocaleDateString('en-US', { 
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })} at {option.time}
                </p>
                <button
                  onClick={() => setActiveCalendar(index)}
                  className="w-full mt-2 text-blue-600 hover:text-blue-800 text-sm"
                >
                  Change
                </button>
              </div>
            )}

            {/* Calendar for this option */}
            {activeCalendar === index && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                {!option.date ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {/* Month navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                      </button>
                      <h5 className="font-semibold text-gray-900">
                        {monthNames[currentCalendarDate.getMonth()]} {currentCalendarDate.getFullYear()}
                      </h5>
                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>

                    {/* Week days */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekDays.map((day) => (
                        <div key={day} className="h-6 flex items-center justify-center text-xs font-medium text-gray-500">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {renderCalendar(index)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Select time for {option.date.toLocaleDateString('en-US')}</h5>
                    <div className="grid grid-cols-3 gap-2">
                      {availableHours.map((hour) => (
                        <button
                          key={hour}
                          onClick={() => handleTimeSelect(hour, index)}
                          className="p-2 text-sm font-medium rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          {hour}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex space-x-3">
        <button
          onClick={handlePrevious}
          className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={selectedOptions.filter(opt => opt.date && opt.time).length === 0}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Your information</h3>
        <p className="text-gray-600">Please provide your contact details</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Doe"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-1" />
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="john.doe@company.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-1" />
            Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="+1 555 123 4567"
            required
          />
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={handlePrevious}
          className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-green-600" />
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Great!</h3>
        <p className="text-gray-600">Your call request has been submitted successfully</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-3">Request Summary</h4>
        <div className="text-left space-y-2 text-sm">
          <div><span className="font-medium">Customer:</span> {promoter.name}</div>
          <div><span className="font-medium">Company:</span> {promoter.company}</div>
          <div><span className="font-medium">Your name:</span> {formData.firstName} {formData.lastName}</div>
          <div><span className="font-medium">Options provided:</span> {selectedOptions.filter(opt => opt.date && opt.time).length}</div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h5 className="font-medium text-gray-900 mb-2">Your suggested times:</h5>
          <div className="space-y-1">
            {selectedOptions.filter(opt => opt.date && opt.time).map((option, index) => (
              <p key={index} className="text-sm text-gray-700">
                • {option.date!.toLocaleDateString('en-US', { 
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })} at {option.time}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-700">
          You will receive a confirmation email at <strong>{formData.email}</strong> with the call details.
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-6">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>
      
      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
          <span>Certified by</span>
          <img 
            src="/logo-color-favicone.png" 
            alt="Clareefai" 
            className="h-4 w-4"
          />
          <span className="font-medium">Clareefai</span>
        </div>
      </div>
    </div>
  );
};

export default OneToOneCallWidget;